<template>
  <div id="password-reset-form-component">
    <div class="w-full max-w-xl p-6 space-y-8 m-auto sm:p-8 bg-white rounded-lg shadow dark:bg-gray-800">
      <h2 class="text-2xl font-bold text-gray-900 dark:text-white">
        Reset your password
      </h2>
      
      <!-- Step 1: Request Password Reset -->
      <form v-if="!resetRequested && !resettingPassword" @submit.prevent="handleRequestReset" class="mt-8 space-y-6">
        <div>
          <label for="email" class="block mb-2 text-sm font-medium text-gray-900 dark:text-white">Email Address</label>
          <input v-model="email" type="email" id="email" class="bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:ring-primary-500 focus:border-primary-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500" placeholder="Enter your email address" required>
        </div>
        <button type="submit" :disabled="isLoading" class="w-full px-5 py-3 text-base font-medium text-center text-white bg-primary-700 rounded-lg hover:bg-primary-800 focus:ring-4 focus:ring-primary-300 sm:w-auto dark:bg-primary-600 dark:hover:bg-primary-700 dark:focus:ring-primary-800 disabled:opacity-50">
          {{ isLoading ? 'Sending...' : 'Send Reset Link' }}
        </button>
      </form>

      <!-- Step 2: Reset Request Sent -->
      <div v-if="resetRequested && !resettingPassword" class="text-center space-y-4">
        <div class="p-4 mb-4 text-sm text-green-800 rounded-lg bg-green-50 dark:bg-green-800 dark:text-green-200">
          <strong>Email sent!</strong> Please check your inbox for password reset instructions.
        </div>
        <p class="text-sm text-gray-600 dark:text-gray-400">
          Didn't receive the email? Check your spam folder or 
          <button @click="resetForm" class="text-primary-700 hover:underline dark:text-primary-500">try again</button>.
        </p>
      </div>

      <!-- Step 3: Reset Password with Token -->
      <form v-if="resettingPassword" @submit.prevent="handlePasswordReset" class="mt-8 space-y-6">
        <div>
          <label for="newPassword" class="block mb-2 text-sm font-medium text-gray-900 dark:text-white">New Password</label>
          <input v-model="newPassword" type="password" id="newPassword" class="bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:ring-primary-500 focus:border-primary-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500" placeholder="••••••••" required minlength="8">
        </div>
        <div>
          <label for="confirmPassword" class="block mb-2 text-sm font-medium text-gray-900 dark:text-white">Confirm New Password</label>
          <input v-model="confirmPassword" type="password" id="confirmPassword" class="bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:ring-primary-500 focus:border-primary-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500" placeholder="••••••••" required minlength="8">
        </div>
        <div v-if="passwordError" class="p-4 mb-4 text-sm text-red-800 rounded-lg bg-red-50 dark:bg-red-800 dark:text-red-200">
          {{ passwordError }}
        </div>
        <button type="submit" :disabled="isLoading || newPassword !== confirmPassword" class="w-full px-5 py-3 text-base font-medium text-center text-white bg-primary-700 rounded-lg hover:bg-primary-800 focus:ring-4 focus:ring-primary-300 sm:w-auto dark:bg-primary-600 dark:hover:bg-primary-700 dark:focus:ring-primary-800 disabled:opacity-50">
          {{ isLoading ? 'Resetting...' : 'Reset Password' }}
        </button>
      </form>

      <!-- Success Message -->
      <div v-if="resetSuccess" class="text-center space-y-4">
        <div class="p-4 mb-4 text-sm text-green-800 rounded-lg bg-green-50 dark:bg-green-800 dark:text-green-200">
          <strong>Success!</strong> Your password has been reset successfully.
        </div>
        <a href="/login" class="inline-block px-5 py-3 text-base font-medium text-center text-white bg-primary-700 rounded-lg hover:bg-primary-800 focus:ring-4 focus:ring-primary-300 dark:bg-primary-600 dark:hover:bg-primary-700 dark:focus:ring-primary-800">
          Back to Login
        </a>
      </div>

      <!-- Back to Login Link -->
      <div v-if="!resetSuccess" class="text-sm font-medium text-gray-500 dark:text-gray-400 text-center">
        Remember your password? <a href="/login" class="text-primary-700 hover:underline dark:text-primary-500">Back to Login</a>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  data() {
    return {
      email: '',
      newPassword: '',
      confirmPassword: '',
      resetRequested: false,
      resettingPassword: false,
      resetSuccess: false,
      isLoading: false,
      passwordError: '',
      resetToken: null,
      resetKey: null,
    };
  },
  mounted() {
    // Check if we have reset parameters in URL
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const key = urlParams.get('key');
    
    if (token && key) {
      this.resetToken = token;
      this.resetKey = key;
      this.resettingPassword = true;
    }
  },
  methods: {
    async handleRequestReset() {
      this.isLoading = true;
      this.passwordError = '';

      try {
        const response = await axios.post('/wp-json/custom/v1/password-reset/request', {
          email: this.email,
        });

        console.log('Reset request sent:', response.data);
        this.resetRequested = true;

      } catch (error) {
        console.error('Reset request failed:', error.response ? error.response.data : error.message);
        this.passwordError = error.response ? error.response.data.message : 'Failed to send reset email. Please try again.';
      } finally {
        this.isLoading = false;
      }
    },

    async handlePasswordReset() {
      this.isLoading = true;
      this.passwordError = '';

      // Validate passwords match
      if (this.newPassword !== this.confirmPassword) {
        this.passwordError = 'Passwords do not match.';
        this.isLoading = false;
        return;
      }

      // Validate password strength
      if (this.newPassword.length < 8) {
        this.passwordError = 'Password must be at least 8 characters long.';
        this.isLoading = false;
        return;
      }

      try {
        const response = await axios.post('/wp-json/custom/v1/password-reset/reset', {
          token: this.resetToken,
          key: this.resetKey,
          password: this.newPassword,
        });

        console.log('Password reset successful:', response.data);
        this.resetSuccess = true;
        this.resettingPassword = false;

      } catch (error) {
        console.error('Password reset failed:', error.response ? error.response.data : error.message);
        this.passwordError = error.response ? error.response.data.message : 'Password reset failed. Please try again or request a new reset link.';
      } finally {
        this.isLoading = false;
      }
    },

    resetForm() {
      this.email = '';
      this.resetRequested = false;
      this.passwordError = '';
      this.isLoading = false;
    },
  },
};
</script>

<style scoped>
/* Add custom styles here */
</style>
